import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TourCard } from "./components/TourCard";
import { CalendarModal } from "./components/CalendarModal";
import { useTranslation } from "react-i18next";

const SelectTicket: React.FC = () => {
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [selectedTours, setSelectedTours] = useState<{
    [key: number]: boolean;
  }>({});
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tours = [
    {
      images: "https://example.com/image1.jpg",
      name: "Deluxe City Tour",
      date: null,
      id: 1,
      success: false,
      message: "Please select time first",
      cardInfo: [{ title: "Package", value: "Package" }],
    },
    {
      images: "https://example.com/image1.jpg",
      name: "Deluxe City Pass",
      date: "12/11/2012",
      id: 2,
      success: true,
      message: "",
      cardInfo: [{ title: "Package", value: "Package" }],
    },
    {
      images: "https://example.com/image1.jpg",
      name: "Deluxe City Product",
      id: 3,
      date: "12/11/2012",
      success: true,
      message: "",
      cardInfo: [
        { title: "Package", value: "Package" },
        { title: "Date and time", value: "10/12/2024" },
        { title: "Guest", value: "Package" },
        { title: "Data package", value: "Package" },
      ],
    },
  ];

  const openPopup = () => {
    if (Object.values(selectedTours).filter(Boolean).length >= 1) {
      confirmAndRedirect();
    } else {
      setPopupVisible(true);
    }
  };

  const closePopup = () => {
    setPopupVisible(false);
  };

  const confirmAndRedirect = () => {
    closePopup();
    navigate("/ticket-check/my-booking");
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const handleDateSelect = (date: number) => {
    setSelectedDate(new Date(selectedDate.setDate(date)));
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const handleMonthChange = (increment: number) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setSelectedDate(newDate);
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsAllChecked(isChecked);
    setSelectedTours(
      tours.reduce(
        (acc, tour) => {
          acc[tour.id] = isChecked;
          return acc;
        },
        {} as { [key: number]: boolean },
      ),
    );
  };

  const handleTourChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    tourId: number,
  ) => {
    const isChecked = e.target.checked;
    setSelectedTours((s) => ({
      ...s,
      [tourId]: isChecked,
    }));
  };

  useEffect(() => {
    const allSelected = tours.every((tour) => selectedTours[tour.id]);
    setIsAllChecked(allSelected);
  }, [selectedTours]);

  return (
    <div className="mx-auto max-w-[75rem] py-8">
      <h1 className="mb-8 text-[1.875rem] font-semibold">
        {t("Select product to receive ticket")}
      </h1>

      <div className="flex gap-[2rem]">
        {/* Left Section */}
        <div className="flex w-[51.0625rem] flex-col gap-[3.375rem]">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="select-all"
              className="custom-checkbox"
              checked={isAllChecked}
              onChange={handleSelectAllChange}
            />
            <label
              htmlFor="select-all"
              className="ps-[1rem] text-lg font-medium"
            >
              {t("ALL")}
            </label>
          </div>
          <div className="flex flex-col items-center">
            {tours.map((tour) => (
              <TourCard
                key={tour.id}
                {...tour}
                isChecked={selectedTours[tour.id] || false}
                onCheckboxChange={handleTourChange}
                onSelectTime={toggleModal}
              />
            ))}
          </div>
        </div>

        {/* Right Section */}
        <div className="h-[8.8125rem] w-[21.25rem] rounded-[1.25rem] border p-[1.5rem]">
          <h2 className="mb-4 text-lg font-medium">
            {t("Total ")}({Object.values(selectedTours).filter(Boolean).length})
          </h2>
          <button
            type="button"
            className="w-full rounded-[1.5625rem] bg-black py-2 text-white hover:bg-gray-800"
            onClick={openPopup}
          >
            {t("Receive ticket")}
          </button>
        </div>

        {isPopupVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="popup-modal w-[22.5rem] gap-[1.5rem] rounded-[1.25rem] bg-white p-[2rem] pb-[1rem] pt-[1.5rem]">
              <div className="text-center">
                <div className="mb-1 flex justify-center">
                  <img
                    src="../../../assets/icons/question.svg"
                    className="h-[3rem] w-[3rem] rounded-[0.5rem] object-cover"
                    alt="question icon"
                  />
                </div>
                <p className="mb-[1.5rem] text-lg">
                  {t("This note was written by admin")}
                </p>
                <div className="flex justify-between space-x-[1rem]">
                  <button
                    className="h-[3rem] w-[18.5rem] rounded-[1.953rem] bg-[#E9EAEB] text-base font-normal text-[#232323]"
                    onClick={closePopup}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    className="h-[3rem] w-[18.5rem] rounded-[1.953rem] bg-primary text-base font-normal text-[#232323]"
                    onClick={closePopup}
                  >
                    {t("Confirm")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <CalendarModal
          isOpen={isModalOpen}
          onClose={toggleModal}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          onDateSelect={handleDateSelect}
          onTimeSelect={handleTimeSelect}
          onMonthChange={handleMonthChange}
        />
      </div>
    </div>
  );
};

export default SelectTicket;
